<!-- Reusable two-month calendar grid, ported from renderMonthCalendar() in DateRangePicker.tsx.
     Used 4 times below (main left/right, compare left/right) via ngTemplateOutlet.
     SETTE redesign (CLIPPING-598): token-by-token restyle, same interaction model throughout -
     trigger is a FilterChip, popovers follow the Popover spec (bg-surface border-line rounded-xl
     shadow-pop), selected day is the solid blue pill, the range between is bg-blue-tint. -->
<ng-template #monthCalendarTpl let-monthData="monthData" let-isCompare="isCompare">
	<div class="w-64 select-none">
		<div class="text-center font-medium text-ink text-xs py-1 mb-2">{{ monthNames[monthData.month] }} {{ monthData.year }}</div>

		<div class="grid grid-cols-7 gap-1 text-center text-[10px] font-medium text-ink-subtle mb-1">
			@for (w of weekdayNames; track w) {
			<div class="py-0.5">{{ w }}</div>
			}
		</div>

		<div class="grid grid-cols-7 gap-y-1 gap-x-0.5 text-center text-xs">
			@for (day of monthData.days; track day.date.getTime()) {
			<button
				type="button"
				(click)="selectDate(day.date, isCompare)"
				(mouseenter)="onDayHover(day.date, isCompare)"
				class="h-7 w-full flex items-center justify-center text-[11px] transition-colors cursor-pointer"
				[class]="dayCellClasses(day, isCompare)">
				<span>{{ day.date.getDate() }}</span>
			</button>
			}
		</div>
	</div>
</ng-template>

<div class="flex flex-wrap items-center gap-2">
	<!-- ============================================================= -->
	<!-- 1. DATE RANGE SELECTOR TRIGGER & 2-MONTH CALENDAR POPOVER -->
	<!-- ============================================================= -->
	<div class="relative" #pickerContainer>
		<button
			type="button"
			(click)="toggleOpen()"
			class="flex items-center gap-2 border rounded-full px-3 h-8 transition-colors cursor-pointer text-xs"
			[class]="isOpen
				? 'border-blue bg-blue-tint text-blue font-medium'
				: 'border-line-strong bg-surface text-ink font-medium'">
			<mat-icon class="!w-3.5 !h-3.5 !text-sm shrink-0" [class]="isOpen ? 'text-blue' : 'text-ink-subtle'">calendar_today</mat-icon>
			<span class="truncate max-w-[240px] sm:max-w-none">{{ triggerLabel }}</span>
			<mat-icon class="!w-3 !h-3 !text-xs text-ink-subtle shrink-0 transition-transform duration-150" [class.rotate-180]="isOpen">expand_more</mat-icon>
		</button>

		@if (isOpen) {
		<div class="absolute left-0 top-full mt-1 bg-surface border border-line rounded-xl shadow-pop z-50 p-4 flex flex-col md:flex-row gap-4 min-w-[320px] md:min-w-[680px]">
			<!-- Left Quick Presets Column -->
			<div class="w-full md:w-36 shrink-0 border-b md:border-b-0 md:border-r border-line pb-3 md:pb-0 md:pr-3 flex flex-col justify-between">
				<div>
					<div class="overline-label text-ink-subtle px-2 mb-2">{{ 'ANALYTICS_DATE_PICKER.QUICK_PRESETS' | translate }}</div>
					<div class="grid grid-cols-2 md:grid-cols-1 gap-1">
						@for (preset of quickPresets; track preset.key) {
						<button
							type="button"
							(click)="selectPreset(preset)"
							class="text-left px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center justify-between cursor-pointer"
							[class]="activePreset === preset.key
								? 'bg-blue-tint text-blue'
								: 'text-ink-muted hover:bg-surface-gray hover:text-ink'">
							<span>{{ preset.labelKey | translate }}</span>
							@if (activePreset === preset.key) {
							<mat-icon class="!w-3 !h-3 !text-xs text-blue shrink-0">check</mat-icon>
							}
						</button>
						}
					</div>
				</div>
				<div class="mt-4 pt-3 border-t border-line hidden md:block">
					<div class="text-[10px] text-ink-subtle font-normal px-1">{{ 'ANALYTICS_DATE_PICKER.NO_DATE_LIMITS' | translate }}</div>
				</div>
			</div>

			<!-- Right: 2 Months Side by Side -->
			<div class="flex-1 flex flex-col">
				<div class="flex items-center justify-between pb-3 mb-3 border-b border-line">
					<div class="flex items-center gap-2">
						<span class="text-xs font-medium text-ink bg-surface-gray px-2.5 py-1 rounded-full">{{ modalRangePreviewText.text }}</span>
						<span class="text-[11px] font-medium text-blue bg-blue-tint px-2 py-0.5 rounded-full">
							{{ modalRangePreviewText.days }} {{ (modalRangePreviewText.days === 1 ? 'ANALYTICS_DATE_PICKER.DAY' : 'ANALYTICS_DATE_PICKER.DAYS') | translate }}
						</span>
					</div>
					<div class="flex items-center gap-1">
						<button type="button" (click)="prevYear()" [title]="'ANALYTICS_DATE_PICKER.PREVIOUS_YEAR' | translate" class="p-1 rounded-lg hover:bg-surface-gray text-ink-subtle hover:text-ink cursor-pointer">
							<mat-icon class="!w-3.5 !h-3.5 !text-sm">first_page</mat-icon>
						</button>
						<button type="button" (click)="prevMonth()" [title]="'ANALYTICS_DATE_PICKER.PREVIOUS_MONTH' | translate" class="p-1 rounded-lg hover:bg-surface-gray text-ink-subtle hover:text-ink cursor-pointer">
							<mat-icon class="!w-3.5 !h-3.5 !text-sm">chevron_left</mat-icon>
						</button>
						<button type="button" (click)="nextMonth()" [title]="'ANALYTICS_DATE_PICKER.NEXT_MONTH' | translate" class="p-1 rounded-lg hover:bg-surface-gray text-ink-subtle hover:text-ink cursor-pointer">
							<mat-icon class="!w-3.5 !h-3.5 !text-sm">chevron_right</mat-icon>
						</button>
						<button type="button" (click)="nextYear()" [title]="'ANALYTICS_DATE_PICKER.NEXT_YEAR' | translate" class="p-1 rounded-lg hover:bg-surface-gray text-ink-subtle hover:text-ink cursor-pointer">
							<mat-icon class="!w-3.5 !h-3.5 !text-sm">last_page</mat-icon>
						</button>
					</div>
				</div>

				<div class="flex flex-col sm:flex-row items-center sm:items-start justify-center gap-6">
					<ng-container *ngTemplateOutlet="monthCalendarTpl; context: { monthData: leftMonthData, isCompare: false }"></ng-container>
					<ng-container *ngTemplateOutlet="monthCalendarTpl; context: { monthData: rightMonthData, isCompare: false }"></ng-container>
				</div>

				<div class="flex items-center justify-between pt-4 mt-4 border-t border-line">
					<div class="text-[11px] text-ink-subtle">{{ 'ANALYTICS_DATE_PICKER.CLICK_START_END' | translate }}</div>
					<div class="flex items-center gap-2">
						<button type="button" (click)="cancelRange()" class="px-3 h-9 text-xs font-medium text-ink-muted hover:bg-surface-gray rounded-full transition-colors cursor-pointer">{{ 'BUTTON.CANCEL' | translate }}</button>
						<button type="button" (click)="applyRange()" class="px-4 h-9 text-xs font-medium text-on-blue bg-blue hover:bg-blue-hover rounded-full transition-colors cursor-pointer">{{ 'ANALYTICS_DATE_PICKER.APPLY_RANGE' | translate }}</button>
					</div>
				</div>
			</div>
		</div>
		}
	</div>

	<!-- ============================================================= -->
	<!-- 2. COMPARE TO SELECTOR & CUSTOM COMPARISON CALENDAR -->
	<!-- ============================================================= -->
	@if (showCompare) {
	<div class="relative" #compareContainer>
		<button
			type="button"
			(click)="toggleCompareOpen()"
			class="flex items-center gap-1.5 border rounded-full px-3 h-8 transition-colors cursor-pointer text-xs"
			[class]="(isCompareOpen || isCustomComparePickerOpen)
				? 'border-blue bg-blue-tint text-blue'
				: 'border-line-strong bg-surface text-ink'">
			<span class="text-ink-subtle font-medium">{{ 'ANALYTICS_DATE_PICKER.VS' | translate }}</span>
			<span class="font-medium truncate max-w-[210px]">{{ activeCompareLabelShort }}</span>
			<mat-icon class="!w-3 !h-3 !text-xs text-ink-subtle shrink-0 transition-transform duration-150" [class.rotate-180]="isCompareOpen">expand_more</mat-icon>
		</button>

		@if (isCompareOpen && !isCustomComparePickerOpen) {
		<div class="absolute left-0 top-full mt-1 w-80 bg-surface border border-line rounded-xl shadow-pop z-50 p-2 text-xs space-y-1">
			<div class="overline-label text-ink-subtle px-2.5 py-1">{{ 'ANALYTICS_DATE_PICKER.COMPARE_BENCHMARK' | translate }}</div>

			<button
				type="button"
				(click)="selectCompareOption('previous_period')"
				class="w-full text-left p-2.5 rounded-lg transition-colors flex items-center justify-between"
				[class]="compareOption.type === 'previous_period' ? 'bg-blue-tint text-blue' : 'hover:bg-surface-gray text-ink'">
				<div>
					<div class="font-medium flex items-center gap-1">
						<span>{{ 'ANALYTICS_DATE_PICKER.PREVIOUS_PERIOD' | translate }}</span>
						<span class="text-[10px] font-normal text-ink-subtle">{{ 'ANALYTICS_DATE_PICKER.PREVIOUS_PERIOD_DESC' | translate }}</span>
					</div>
					<div class="text-[11px] text-blue font-medium mt-0.5">{{ prevPeriod.label }}</div>
				</div>
				@if (compareOption.type === 'previous_period') {
				<mat-icon class="!w-4 !h-4 !text-base text-blue shrink-0">check</mat-icon>
				}
			</button>

			<button
				type="button"
				(click)="selectCompareOption('same_period_last_year')"
				class="w-full text-left p-2.5 rounded-lg transition-colors flex items-center justify-between"
				[class]="compareOption.type === 'same_period_last_year' ? 'bg-blue-tint text-blue' : 'hover:bg-surface-gray text-ink'">
				<div>
					<div class="font-medium flex items-center gap-1"><span>{{ 'ANALYTICS_DATE_PICKER.SAME_PERIOD_LAST_YEAR' | translate }}</span></div>
					<div class="text-[11px] text-blue font-medium mt-0.5">{{ lastYearPeriod.label }}</div>
				</div>
				@if (compareOption.type === 'same_period_last_year') {
				<mat-icon class="!w-4 !h-4 !text-base text-blue shrink-0">check</mat-icon>
				}
			</button>

			<button
				type="button"
				(click)="openCustomCompare()"
				class="w-full text-left p-2.5 rounded-lg transition-colors flex items-center justify-between"
				[class]="compareOption.type === 'custom' ? 'bg-blue-tint text-blue' : 'hover:bg-surface-gray text-ink'">
				<div>
					<div class="font-medium flex items-center gap-1"><span>{{ 'ANALYTICS_DATE_PICKER.CUSTOM_COMPARISON_PERIOD' | translate }}</span></div>
					<div class="text-[11px] text-ink-subtle mt-0.5">
						@if (compareOption.type === 'custom' && compareOption.startDate && compareOption.endDate) {
						{{ 'ANALYTICS_DATE_PICKER.VS' | translate }} {{ formatRange(compareOption.startDate, compareOption.endDate) }}
						} @else {
						{{ 'ANALYTICS_DATE_PICKER.CHOOSE_CUSTOM_DATES' | translate }}
						}
					</div>
				</div>
				<mat-icon class="!w-4 !h-4 !text-base text-ink-subtle shrink-0">arrow_forward</mat-icon>
			</button>

			<button
				type="button"
				(click)="selectCompareOption('none')"
				class="w-full text-left px-2.5 py-1.5 rounded-lg transition-colors flex items-center justify-between text-ink-subtle hover:bg-surface-gray">
				<span>{{ 'ANALYTICS_DATE_PICKER.NO_COMPARISON' | translate }}</span>
				@if (compareOption.type === 'none') {
				<mat-icon class="!w-3.5 !h-3.5 !text-sm text-ink-muted shrink-0">check</mat-icon>
				}
			</button>
		</div>
		}

		@if (isCustomComparePickerOpen) {
		<div class="absolute left-0 top-full mt-1 bg-surface border border-line rounded-xl shadow-pop z-50 p-4 flex flex-col min-w-[320px] md:min-w-[580px]">
			<div class="flex items-center justify-between pb-3 mb-3 border-b border-line">
				<div class="flex items-center gap-2">
					<span class="overline-label text-ink-subtle">{{ 'ANALYTICS_DATE_PICKER.CUSTOM_BENCHMARK_PERIOD' | translate }}</span>
					<span class="text-xs font-medium text-blue bg-blue-tint px-2.5 py-1 rounded-full">{{ 'ANALYTICS_DATE_PICKER.VS' | translate }} {{ compareModalRangePreviewText.text }}</span>
					<span class="text-[11px] font-medium text-ink-muted bg-surface-gray px-2 py-0.5 rounded-full">{{ compareModalRangePreviewText.days }} {{ (compareModalRangePreviewText.days === 1 ? 'ANALYTICS_DATE_PICKER.DAY' : 'ANALYTICS_DATE_PICKER.DAYS') | translate }}</span>
				</div>
				<div class="flex items-center gap-1">
					<button type="button" (click)="prevYear(true)" [title]="'ANALYTICS_DATE_PICKER.PREVIOUS_YEAR' | translate" class="p-1 rounded-lg hover:bg-surface-gray text-ink-subtle hover:text-ink cursor-pointer">
						<mat-icon class="!w-3.5 !h-3.5 !text-sm">first_page</mat-icon>
					</button>
					<button type="button" (click)="prevMonth(true)" [title]="'ANALYTICS_DATE_PICKER.PREVIOUS_MONTH' | translate" class="p-1 rounded-lg hover:bg-surface-gray text-ink-subtle hover:text-ink cursor-pointer">
						<mat-icon class="!w-3.5 !h-3.5 !text-sm">chevron_left</mat-icon>
					</button>
					<button type="button" (click)="nextMonth(true)" [title]="'ANALYTICS_DATE_PICKER.NEXT_MONTH' | translate" class="p-1 rounded-lg hover:bg-surface-gray text-ink-subtle hover:text-ink cursor-pointer">
						<mat-icon class="!w-3.5 !h-3.5 !text-sm">chevron_right</mat-icon>
					</button>
					<button type="button" (click)="nextYear(true)" [title]="'ANALYTICS_DATE_PICKER.NEXT_YEAR' | translate" class="p-1 rounded-lg hover:bg-surface-gray text-ink-subtle hover:text-ink cursor-pointer">
						<mat-icon class="!w-3.5 !h-3.5 !text-sm">last_page</mat-icon>
					</button>
				</div>
			</div>

			<div class="flex flex-col sm:flex-row items-center sm:items-start justify-center gap-6">
				<ng-container *ngTemplateOutlet="monthCalendarTpl; context: { monthData: compareLeftMonthData, isCompare: true }"></ng-container>
				<ng-container *ngTemplateOutlet="monthCalendarTpl; context: { monthData: compareRightMonthData, isCompare: true }"></ng-container>
			</div>

			<div class="flex items-center justify-between pt-4 mt-4 border-t border-line">
				<button type="button" (click)="backToCompareOptions()" class="text-xs text-blue hover:underline font-medium">← {{ 'ANALYTICS_DATE_PICKER.BACK_TO_OPTIONS' | translate }}</button>
				<div class="flex items-center gap-2">
					<button type="button" (click)="cancelCustomCompare()" class="px-3 h-9 text-xs font-medium text-ink-muted hover:bg-surface-gray rounded-full cursor-pointer">{{ 'BUTTON.CANCEL' | translate }}</button>
					<button type="button" (click)="applyCustomCompare()" class="px-4 h-9 text-xs font-medium text-on-blue bg-blue hover:bg-blue-hover rounded-full cursor-pointer">{{ 'ANALYTICS_DATE_PICKER.APPLY_BENCHMARK' | translate }}</button>
				</div>
			</div>
		</div>
		}
	</div>
	}
</div>
